import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../context/UserContext';
import { useAnnotations } from '../context/AnnotationsContext';
import { bookmarkApi, highlightApi } from '../services/api';
import { applyRange, chapterKey, mergeVerseHighlights, toVersePayload, type SelectionPiece } from '../services/highlights';
import { bookmarksKey, fetchChapterHighlights, highlightsKey } from './queries';
import type { Bookmark, HighlightColor, VerseHighlights, VerseHighlightsPayload, VerseRef } from '../types';

const NO_HIGHLIGHTS: VerseHighlights = {};
const NO_BOOKMARKS: Bookmark[] = [];

/** The logged-in user's id, or null for an anonymous reader, whose annotations stay in memory. */
function useSignedInUserId(): number | null {
  const { user, isAuthenticated } = useUser();
  return isAuthenticated() && user ? user.id : null;
}

/**
 * The reader's highlights of one chapter in one translation, and a way to paint or erase a selection there. A
 * logged-in reader's highlights are saved to the account; an anonymous reader's last until the page is reloaded.
 */
export function useChapterHighlights(bibleId: number, bookId: number, chapter: number) {
  const queryClient = useQueryClient();
  const userId = useSignedInUserId();
  const { localHighlights, updateLocalHighlights } = useAnnotations();
  const validChapter = bibleId > 0 && bookId > 0 && chapter > 0;

  const { data } = useQuery({
    queryKey: highlightsKey(userId ?? 0, bibleId, bookId, chapter),
    queryFn: () => fetchChapterHighlights(bibleId, bookId, chapter),
    enabled: userId !== null && validChapter,
  });

  const { mutate: saveVerses } = useMutation({
    mutationFn: (verses: VerseHighlightsPayload[]) => highlightApi.replace(verses),
    onError: (err) => {
      console.error('Failed to save highlights:', (err as Error).message);
      // The cache was updated ahead of the request; bring it back in line with what the account really has.
      queryClient.invalidateQueries({ queryKey: ['highlights'] });
    },
  });

  const highlights = userId === null
    ? localHighlights[chapterKey(bibleId, bookId, chapter)] ?? NO_HIGHLIGHTS
    : data ?? NO_HIGHLIGHTS;

  const applyHighlight = useCallback((pieces: SelectionPiece[], color: HighlightColor | null) => {
    if (!validChapter || pieces.length === 0) return;
    // Each save replaces whole verses, so writing before the account's highlights have loaded would wipe them.
    if (userId !== null && data === undefined) return;

    const changes: VerseHighlights = {};
    for (const piece of pieces) {
      changes[piece.verseNumber] = applyRange(highlights[piece.verseNumber] ?? [], piece.start, piece.end, color);
    }

    if (userId === null) {
      updateLocalHighlights(chapterKey(bibleId, bookId, chapter), changes);
      return;
    }

    queryClient.setQueryData<VerseHighlights>(
      highlightsKey(userId, bibleId, bookId, chapter),
      prev => mergeVerseHighlights(prev ?? {}, changes),
    );
    saveVerses(Object.entries(changes).map(([verse, ranges]) =>
      toVersePayload(bibleId, bookId, chapter, Number(verse), ranges)
    ));
  }, [validChapter, userId, data, highlights, bibleId, bookId, chapter, updateLocalHighlights, queryClient, saveVerses]);

  return { highlights, applyHighlight };
}

const isSameVerse = (a: VerseRef, b: VerseRef) =>
  a.canonicalBookId === b.canonicalBookId && a.chapterNumber === b.chapterNumber && a.verseNumber === b.verseNumber;

/**
 * The reader's bookmarks, newest first. Saved to the account when logged in, otherwise kept until the page is reloaded.
 */
export function useBookmarks() {
  const queryClient = useQueryClient();
  const userId = useSignedInUserId();
  const { localBookmarks, addLocalBookmark, removeLocalBookmark } = useAnnotations();

  const { data } = useQuery({
    queryKey: bookmarksKey(userId ?? 0),
    queryFn: bookmarkApi.getAll,
    enabled: userId !== null,
  });

  const { mutate: createBookmark } = useMutation({
    mutationFn: (verse: VerseRef) => bookmarkApi.create(verse.canonicalBookId, verse.chapterNumber, verse.verseNumber),
    onSuccess: (created) => {
      queryClient.setQueryData<Bookmark[]>(bookmarksKey(userId ?? 0), prev =>
        prev?.some(b => b.id === created.id) ? prev : [created, ...(prev ?? [])]
      );
    },
    onError: (err) => console.error('Failed to add bookmark:', (err as Error).message),
  });

  const { mutate: deleteBookmark } = useMutation({
    mutationFn: (bookmark: Bookmark) => bookmarkApi.delete(bookmark.id),
    onSuccess: (_data, removed) => {
      queryClient.setQueryData<Bookmark[]>(bookmarksKey(userId ?? 0), prev => prev?.filter(b => b.id !== removed.id));
    },
    onError: (err) => console.error('Failed to remove bookmark:', (err as Error).message),
  });

  const bookmarks = userId === null ? localBookmarks : data ?? NO_BOOKMARKS;

  const removeBookmark = useCallback((bookmark: Bookmark) => {
    if (userId === null) {
      removeLocalBookmark(bookmark);
    } else {
      deleteBookmark(bookmark);
    }
  }, [userId, removeLocalBookmark, deleteBookmark]);

  const toggleBookmark = useCallback((verse: VerseRef) => {
    const existing = bookmarks.find(b => isSameVerse(b, verse));
    if (existing) {
      removeBookmark(existing);
    } else if (userId === null) {
      addLocalBookmark(verse);
    } else {
      createBookmark(verse);
    }
  }, [bookmarks, removeBookmark, userId, addLocalBookmark, createBookmark]);

  return { bookmarks, toggleBookmark, removeBookmark, isSavedToAccount: userId !== null };
}
